// Chamadas dos Roteiros comerciais (ações `rot_*` em /comercial/propostas-geradas).
// Uma função por ação; o erro vem com a frase do servidor (`erroDe`), que é o que a tela mostra.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fraseDeFalha } from '../comercial-onde-ganha/ondeGanhaRegras';
import type { Formulario, Listagem, Roteiro, VersaoCompleta, VersaoResumo } from './regras';

async function post<T>(body: Record<string, unknown>, timeout = 30000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

interface ErroHttp {
  response?: { status?: number; data?: { error?: unknown; erro?: unknown } };
  code?: string;
  message?: string;
}

export const erroDe = (e: unknown): string => {
  const x = e as ErroHttp;
  const d = x?.response?.data;
  const doServidor = d?.error || d?.erro;
  if (typeof doServidor === 'string' && doServidor.trim()) return doServidor;
  return fraseDeFalha(x?.response?.status ?? null, !!x?.response, x?.code, x?.message);
};

/** 409 = outra pessoa mudou o roteiro antes. */
export const ehConflito = (e: unknown): boolean => (e as ErroHttp)?.response?.status === 409;
/** 403 = sem a permissão da ação. */
export const ehSemAcesso = (e: unknown): boolean => (e as ErroHttp)?.response?.status === 403;
/** 503 = a página ainda está sendo instalada no servidor. */
export const ehEmInstalacao = (e: unknown): boolean => (e as ErroHttp)?.response?.status === 503;

// ── Ações ────────────────────────────────────────────────────────────────────
/** Vem tudo (inclusive arquivados), já na ordem certa. */
export const listar = (): Promise<Listagem> => post<Listagem>({ action: 'rot_listar' });

export type DadosSalvar = Partial<Formulario> & { id?: number; ordem?: number; visto?: { atualizado_em: string } };

/** Sem `id` cria (o servidor gera o código); com `id` edita e exige `visto.atualizado_em`. */
export const salvar = (dados: DadosSalvar): Promise<{ roteiro: Roteiro }> =>
  post<{ roteiro: Roteiro }>({ action: 'rot_salvar', ...dados });

export const versoes = (id: number): Promise<{ versoes: VersaoResumo[] }> =>
  post<{ versoes: VersaoResumo[] }>({ action: 'rot_versoes', id });

export const versao = (id: number, numero: number): Promise<{ versao: VersaoCompleta }> =>
  post<{ versao: VersaoCompleta }>({ action: 'rot_versao', id, versao: numero });

export const restaurar = (id: number, numero: number, visto: { atualizado_em: string }): Promise<{ roteiro: Roteiro }> =>
  post<{ roteiro: Roteiro }>({ action: 'rot_restaurar', id, versao: numero, visto });
